import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { XMarkIcon } from '@heroicons/react/24/outline';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...userData } = formData;
      
      const result = await register(userData);
      
      if (result.success) {
        navigate('/app/dashboard');
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-[1408px] mx-auto">
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-white">
          Create your account
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-[#101729] px-6 py-12 shadow sm:rounded-lg sm:px-12 border border-gray-800">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium leading-6 text-[#D4D2ED]">
                  First name
                </label>
                <div className="mt-2">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 py-1.5 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium leading-6 text-[#D4D2ED]">
                  Last name
                </label>
                <div className="mt-2">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 py-1.5 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium leading-6 text-[#D4D2ED]">
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label htmlFor="companyName" className="block text-sm font-medium leading-6 text-[#D4D2ED]">
                Company name
              </label>
              <div className="mt-2">
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium leading-6 text-[#D4D2ED]">
                Password
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                />
              </div>
              <p className="mt-1 text-sm text-[#D4D2ED]/70">At least 8 characters</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium leading-6 text-[#D4D2ED]">
                Confirm password
              </label>
              <div className="mt-2">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="agree-terms"
                name="agree-terms"
                type="checkbox"
                required
                className="h-4 w-4 rounded border-gray-700 bg-[#101729] text-[#19BC9C] focus:ring-[#19BC9C]"
              />
              <label htmlFor="agree-terms" className="ml-3 block text-sm leading-6 text-[#D4D2ED]">
                I agree to the{' '}
                <button 
                  type="button" 
                  onClick={() => setTosModalOpen(true)} 
                  className="font-semibold text-[#ABA2FE] hover:text-[#8A7EF8]"
                >
                  Terms of Service
                </button>{' '}
                and{' '}
                <button 
                  type="button" 
                  onClick={() => setPrivacyModalOpen(true)} 
                  className="font-semibold text-[#ABA2FE] hover:text-[#8A7EF8]"
                >
                  Privacy Policy
                </button>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full justify-center rounded-full bg-[#134448] hover:bg-[#0D3135] px-3 py-2 text-sm font-semibold leading-6 text-[#1ABC9D] shadow-md disabled:bg-[#0D3135]/70 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>
        </div>

        <p className="mt-10 text-center text-sm text-[#D4D2ED]">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold leading-6 text-[#ABA2FE] hover:text-[#8A7EF8]">
            Sign in
          </Link>
        </p>
      </div>
      {/* Terms of Service Modal */}
      {tosModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900/80 transition-opacity" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="relative inline-block align-bottom bg-[#101729] rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-[#101729] text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                  onClick={() => setTosModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="mt-3 sm:mt-0 sm:text-left">
                <h3 className="text-2xl font-semibold leading-6 text-white mb-4">
                  RocketScanner.com - Terms of Service
                </h3>
                
                <div className="mt-4 text-sm text-[#D4D2ED] h-[500px] overflow-y-auto pr-4">
                  <p className="mb-4">Effective Date: May 12, 2025</p>
                  
                  <p className="mb-4">By accessing or using RocketScanner.com (the "Platform"), you agree to be bound by the following Terms of Service ("Terms"). These Terms form a legal agreement between you ("User", "you") and Mariner Auction Marketplace Limited ("RocketScanner", "we", "us", or "our"), the operator of this Platform.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">1. Eligibility</h4>
                  <p className="mb-4">You must be at least 18 years old or have the authority of a legal guardian to use RocketScanner. By using the Platform, you affirm that you meet this requirement.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">2. Account Registration & Access</h4>
                  <p className="mb-4">You agree to provide accurate, complete, and current account information.</p>
                  <p className="mb-4">You are responsible for safeguarding your login credentials.</p>
                  <p className="mb-4">We may suspend or terminate access for any unauthorized use or violation of these Terms.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">3. Use of Services</h4>
                  <p className="mb-4">RocketScanner provides AI-enhanced inventory scanning, categorization, and export features.</p>
                  <p className="mb-4">Use of the service is limited to lawful purposes. You may not:</p>
                  <ul className="list-disc ml-6 mb-4 space-y-2">
                    <li>Upload illegal or infringing content.</li>
                    <li>Use bots, scrapers, or reverse-engineering tools.</li>
                    <li>Attempt to gain unauthorized access to other accounts.</li>
                  </ul>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">4. Subscription & Billing</h4>
                  <p className="mb-4">RocketScanner operates on a subscription basis. By subscribing, you agree to recurring charges unless canceled.</p>
                  <p className="mb-4">Taxes are applied based on your location. Late or failed payments may result in suspended service.</p>
                  <p className="mb-4">Plans auto-renew unless explicitly canceled by the user prior to the renewal date.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">5. Intellectual Property</h4>
                  <p className="mb-4">All intellectual property rights in RocketScanner, including the software, graphics, and AI components, are owned by Mariner Auction Marketplace Limited.</p>
                  <p className="mb-4">You retain rights to the content you upload, but you grant RocketScanner a license to use it for providing the service.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">6. Data Handling & Security</h4>
                  <p className="mb-4">All user-uploaded data is handled in accordance with our Privacy Policy (see below).</p>
                  <p className="mb-4">You are responsible for backing up your own data. RocketScanner is not liable for any data loss unless due to gross negligence.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">7. Termination</h4>
                  <p className="mb-4">We reserve the right to terminate or suspend access to your account at our discretion for violations of these Terms.</p>
                  <p className="mb-4">You may cancel your account at any time. Upon termination, your data may be deleted from our servers after a 30-day grace period.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">8. Limitation of Liability</h4>
                  <p className="mb-4">To the fullest extent permitted by law, RocketScanner and its affiliates are not liable for:</p>
                  <ul className="list-disc ml-6 mb-4 space-y-2">
                    <li>Any indirect, incidental, or consequential damages;</li>
                    <li>Data loss;</li>
                    <li>Loss of profits or reputation;</li>
                    <li>Damages from unauthorized access to your account.</li>
                  </ul>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">9. Indemnification</h4>
                  <p className="mb-4">You agree to indemnify and hold harmless RocketScanner and its affiliates from any claims, damages, or legal fees arising from your use of the Platform or your violation of these Terms.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">10. Governing Law</h4>
                  <p className="mb-4">These Terms are governed by the laws of the Province of Nova Scotia and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts located in Halifax, Nova Scotia.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">11. Changes to These Terms</h4>
                  <p className="mb-4">We may update these Terms from time to time. Continued use of the Platform after changes means you accept the new Terms. We will notify you of material changes via email or within the Platform.</p>
                </div>
                
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="w-full rounded-full bg-[#19BC9C] px-4 py-2 text-sm font-medium text-[#0B0F19] hover:bg-[#16a085] focus:outline-none"
                    onClick={() => setTosModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {privacyModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900/80 transition-opacity" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="relative inline-block align-bottom bg-[#101729] rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-[#101729] text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                  onClick={() => setPrivacyModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="mt-3 sm:mt-0 sm:text-left">
                <h3 className="text-2xl font-semibold leading-6 text-white mb-4">
                  RocketScanner.com – Privacy Policy
                </h3>
                
                <div className="mt-4 text-sm text-[#D4D2ED] h-[500px] overflow-y-auto pr-4">
                  <p className="mb-4">Effective Date: May 12, 2025</p>
                  <p className="mb-4">
                    <strong>Data Controller:</strong> Mariner Auction Marketplace Limited<br />
                    <strong>Mailing Address:</strong> 848 Main Street, Dartmouth, NS, B2W 6J6, Canada<br />
                    <strong>Email:</strong> support@rocketscanner.com<br />
                    <strong>Phone:</strong> (902) 292-2902
                  </p>
                  
                  <p className="mb-4">RocketScanner.com is committed to protecting your privacy. This Privacy Policy outlines how we collect, use, store, and protect your information when you use our services.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">1. What Information We Collect</h4>
                  <p className="mb-2">We collect the following types of personal and usage data:</p>
                  
                  <h5 className="text-md font-semibold text-white mb-2">a. Account Information</h5>
                  <ul className="list-disc ml-6 mb-4 space-y-1">
                    <li>Full Name</li>
                    <li>Email Address</li>
                    <li>Company Name</li>
                    <li>Role or Title</li>
                    <li>Phone Number</li>
                    <li>Billing Address</li>
                  </ul>
                  
                  <h5 className="text-md font-semibold text-white mb-2">b. Inventory & Scan Data</h5>
                  <ul className="list-disc ml-6 mb-4 space-y-1">
                    <li>Product Names, Descriptions, and Tags</li>
                    <li>Photos Uploaded</li>
                    <li>UPC or Barcode Data</li>
                    <li>Scan History and List Metadata</li>
                  </ul>
                  
                  <h5 className="text-md font-semibold text-white mb-2">c. Technical & Usage Data</h5>
                  <ul className="list-disc ml-6 mb-4 space-y-1">
                    <li>IP Address</li>
                    <li>Browser Type and Version</li>
                    <li>Device Type and Operating System</li>
                    <li>Session Activity and Access Logs</li>
                  </ul>
                  
                  <h5 className="text-md font-semibold text-white mb-2">d. Payment Information</h5>
                  <ul className="list-disc ml-6 mb-4 space-y-1">
                    <li>Stripe handles all payment processing securely.</li>
                    <li>We do not store credit card numbers.</li>
                  </ul>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">2. How We Use Your Information</h4>
                  <p className="mb-2">We use the collected information for the following purposes:</p>
                  <ul className="list-disc ml-6 mb-4 space-y-1">
                    <li>To provide and maintain our services</li>
                    <li>To process and complete transactions</li>
                    <li>To send administrative information</li>
                    <li>To improve and personalize user experience</li>
                    <li>To analyze usage patterns and optimize performance</li>
                    <li>To prevent fraudulent activities</li>
                  </ul>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">3. Data Security</h4>
                  <p className="mb-4">We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All data is encrypted in transit and at rest.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">4. Data Retention</h4>
                  <p className="mb-4">We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">5. Your Rights</h4>
                  <p className="mb-2">You have the following rights regarding your personal data:</p>
                  <ul className="list-disc ml-6 mb-4 space-y-1">
                    <li>Right to access your data</li>
                    <li>Right to correct inaccurate data</li>
                    <li>Right to delete your data (where applicable)</li>
                    <li>Right to restrict processing</li>
                    <li>Right to data portability</li>
                    <li>Right to object to processing</li>
                  </ul>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">6. Third-Party Service Providers</h4>
                  <p className="mb-4">We may employ third-party companies to facilitate our service, provide service on our behalf, or assist in analyzing how our service is used. These third parties have access to your personal information only to perform these tasks and are obligated not to disclose or use it for any other purpose.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">7. Changes to This Policy</h4>
                  <p className="mb-4">We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top.</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">8. Contact Us</h4>
                  <p className="mb-4">If you have any questions about this Privacy Policy, please contact us at support@rocketscanner.com or using the contact information provided at the top of this policy.</p>
                </div>
                
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="w-full rounded-full bg-[#19BC9C] px-4 py-2 text-sm font-medium text-[#0B0F19] hover:bg-[#16a085] focus:outline-none"
                    onClick={() => setPrivacyModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
